/**
 * "Modo liga App": el usuario entró al micrositio por una liga que arma la App
 * (pagar/1, pagar/9, pagar/22). Mientras esté activo, solo puede moverse dentro del
 * flujo de pago de ESOS boletos: pagar/* -> carrito-compra -> forma-pago ->
 * venta-exitosa / error-pago. Cualquier otra ruta (home, login, datos-comprador,
 * boletos-colaborador...) la bloquea ModoLigaAppGuard y lo regresa a su liga.
 *
 * POR QUE: desde la liga se iban al home, apartaban otros boletos y revolvían
 * compras de la App con venta digital.
 *
 * Vive en sessionStorage a propósito: es por pestaña y muere al cerrarla. Por eso los
 * sessionStorage.clear() del flujo deben respaldar/restaurar estas llaves (ver
 * LocalstorageService.limpiarCompra).
 *
 * No se reutiliza 'pk_boleto_app': la pone también pagar/23 (preventa, no es App) y
 * el home la borra.
 *
 * TIPOS (cambian que pantallas se permiten, ver ModoLigaAppGuard):
 *   'app'     -> ligas de la App. Se saltan datos-comprador (tiposPago 2/3).
 *   'externa' -> venta-digital-externa (plazas/tiendas). Mismo candado, pero SI pasan por
 *                datos-comprador porque son compradores nuevos (tiposPago 1).
 */
export type TipoModoLiga = 'app' | 'externa';

export class ModoLigaApp {
  /** Escenarios de pagar/:id que genera la App (Carrito.js, EstadoDeCuenta.js, Talonarios.js). */
  static readonly IDS_APP = ['1', '9', '22'];

  private static readonly KEY_ACTIVO = 'modoLigaApp';
  private static readonly KEY_ORIGEN = 'ligaAppOrigen';
  private static readonly KEY_TIPO = 'modoLigaTipo';

  static esLigaApp(id: string): boolean {
    return ModoLigaApp.IDS_APP.includes(id);
  }

  /** Activa el modo y guarda la URL de la liga (ruta del router) para poder regresar a ella. */
  static activar(urlLiga: string, tipo: TipoModoLiga = 'app'): void {
    sessionStorage.setItem(ModoLigaApp.KEY_ACTIVO, '1');
    sessionStorage.setItem(ModoLigaApp.KEY_ORIGEN, urlLiga);
    sessionStorage.setItem(ModoLigaApp.KEY_TIPO, tipo);
  }

  static desactivar(): void {
    sessionStorage.removeItem(ModoLigaApp.KEY_ACTIVO);
    sessionStorage.removeItem(ModoLigaApp.KEY_ORIGEN);
    sessionStorage.removeItem(ModoLigaApp.KEY_TIPO);
  }

  static activo(): boolean {
    return sessionStorage.getItem(ModoLigaApp.KEY_ACTIVO) === '1';
  }

  static tipo(): TipoModoLiga {
    return sessionStorage.getItem(ModoLigaApp.KEY_TIPO) === 'externa' ? 'externa' : 'app';
  }

  /** Ruta de la liga original (ej. /pagar/22/xxxx), o null. */
  static origen(): string | null {
    return sessionStorage.getItem(ModoLigaApp.KEY_ORIGEN);
  }

  /** Para sobrevivir a un sessionStorage.clear(): respaldar antes, restaurar después. */
  static respaldar(): { activo: string | null; origen: string | null; tipo: string | null } {
    return {
      activo: sessionStorage.getItem(ModoLigaApp.KEY_ACTIVO),
      origen: sessionStorage.getItem(ModoLigaApp.KEY_ORIGEN),
      tipo: sessionStorage.getItem(ModoLigaApp.KEY_TIPO),
    };
  }

  static restaurar(respaldo: { activo: string | null; origen: string | null; tipo: string | null }): void {
    if (respaldo.activo) sessionStorage.setItem(ModoLigaApp.KEY_ACTIVO, respaldo.activo);
    if (respaldo.origen) sessionStorage.setItem(ModoLigaApp.KEY_ORIGEN, respaldo.origen);
    if (respaldo.tipo) sessionStorage.setItem(ModoLigaApp.KEY_TIPO, respaldo.tipo);
  }
}
